import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Download, Trash2, FileText, Inbox, Check, X, Eye, Search } from "lucide-react";

const COMPANY_LABELS = { misas: "Misaş", mis_holding: "Mis Holding", koop: "Mis Koop." };
const COMPANY_CLS = { misas: "company-misas", mis_holding: "company-mh", koop: "company-koop" };

const STATUS_META = {
  new:      { label: "Yeni",       cls: "bg-slate-100 text-slate-700 border-slate-200" },
  reviewed: { label: "İncelendi",  cls: "bg-blue-50 text-blue-800 border-blue-200" },
  approved: { label: "Onaylandı",  cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  rejected: { label: "Reddedildi", cls: "bg-red-50 text-red-800 border-red-200" },
};

const FILTERS = [
  { key: "all",      label: "Tümü" },
  { key: "new",      label: "Yeni" },
  { key: "reviewed", label: "İncelendi" },
  { key: "approved", label: "Onaylandı" },
  { key: "rejected", label: "Reddedildi" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch (_) { return iso; }
}

function ApproveDialog({ open, dilekce, onClose, onSubmit }) {
  const [yk, setYk] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => { if (open) { setYk(""); setNote(""); } }, [open]);
  if (!open || !dilekce) return null;
  const submit = () => {
    if (!yk.trim()) { toast.error("Yönetim Kurulu Karar No zorunludur"); return; }
    onSubmit({ status: "approved", yk_karar_no: yk.trim(), review_note: note.trim() });
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="approve-dialog">
      <div className="crisp-card p-6 w-full max-w-md">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Dilekçeyi Onayla</h3>
            <p className="text-xs text-slate-500 mt-0.5">{dilekce.member_ad_soyad} · {COMPANY_LABELS[dilekce.company]} · {dilekce.hisse} hisse</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X size={18}/></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5">Yönetim Kurulu Karar No <span className="text-red-600 font-normal normal-case">(zorunlu)</span></label>
            <input data-testid="approve-yk" className="w-full px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm bg-white"
                   value={yk} onChange={e => setYk(e.target.value)} placeholder="Ör. 2026/07" />
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5">Not (opsiyonel)</label>
            <textarea data-testid="approve-note" className="w-full px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm bg-white min-h-[70px]"
                      value={note} onChange={e => setNote(e.target.value)} placeholder="Örn. hisse bedeli x TL üzerinden ödenmiştir" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50">İptal</button>
          <button onClick={submit} data-testid="approve-submit" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700">
            <Check size={14}/> Onayla
          </button>
        </div>
      </div>
    </div>
  );
}

function RejectDialog({ open, dilekce, onClose, onSubmit }) {
  const [note, setNote] = useState("");
  useEffect(() => { if (open) setNote(""); }, [open]);
  if (!open || !dilekce) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="reject-dialog">
      <div className="crisp-card p-6 w-full max-w-md">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Dilekçeyi Reddet</h3>
            <p className="text-xs text-slate-500 mt-0.5">{dilekce.member_ad_soyad} · {COMPANY_LABELS[dilekce.company]} · {dilekce.hisse} hisse</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X size={18}/></button>
        </div>
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5">Ret Sebebi (opsiyonel)</label>
          <textarea data-testid="reject-note" className="w-full px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm bg-white min-h-[70px]"
                    value={note} onChange={e => setNote(e.target.value)} placeholder="Örn. IBAN doğrulanamadı" />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50">İptal</button>
          <button onClick={() => onSubmit({ status: "rejected", review_note: note.trim() })} data-testid="reject-submit"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700">
            <X size={14}/> Reddet
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DilekceList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [approveOn, setApproveOn] = useState(null);
  const [rejectOn, setRejectOn] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dilekce`, { withCredentials: true });
      setItems(r.data || []);
    } catch (e) {
      toast.error("Dilekçeler yüklenemedi");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const patch = async (id, body) => {
    try {
      const r = await axios.patch(`${API}/dilekce/${id}`, body, { withCredentials: true });
      setItems(items.map(x => x.id === id ? { ...x, ...r.data } : x));
      toast.success("Durum güncellendi");
      setApproveOn(null); setRejectOn(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    }
  };

  const markReviewed = (id) => patch(id, { status: "reviewed" });

  const download = async (id, name) => {
    try {
      const r = await axios.get(`${API}/dilekce/${id}/download`, { responseType: "blob", withCredentials: true });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `${name || "dilekce"}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (_) { toast.error("Dilekçe indirilemedi"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu dilekçe kaydı kalıcı olarak silinsin mi?")) return;
    try {
      await axios.delete(`${API}/dilekce/${id}`, { withCredentials: true });
      setItems(items.filter(x => x.id !== id));
      toast.success("Silindi");
    } catch (_) { toast.error("Silinemedi"); }
  };

  const counts = items.reduce((acc, d) => {
    const s = d.status || "new";
    acc[s] = (acc[s] || 0) + 1; acc.all = (acc.all || 0) + 1;
    return acc;
  }, {});

  const query = q.trim().toLocaleLowerCase("tr-TR");
  const visible = items.filter(d => {
    if (filter !== "all" && (d.status || "new") !== filter) return false;
    if (!query) return true;
    return (
      (d.member_ad_soyad || "").toLocaleLowerCase("tr-TR").includes(query) ||
      (d.member_tc || "").includes(query) ||
      (d.iban || "").toLocaleLowerCase("tr-TR").includes(query)
    );
  });

  return (
    <div className="space-y-6" data-testid="dilekce-list-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Ortak Dilekçeleri</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Gelen Dilekçeler</h1>
          <p className="text-slate-600 mt-2 text-sm">Ortakların kendi paneli üzerinden gönderdiği <b>hisse satış / ayrılış</b> dilekçeleri burada takip edilir.</p>
        </div>
        <div className="text-sm text-slate-500 font-mono-nums">Toplam: {items.length}</div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(f => (
          <button key={f.key} type="button" onClick={() => setFilter(f.key)}
            data-testid={`dilekce-filter-${f.key}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${filter === f.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
            {f.label} <span className="ml-1 opacity-70 font-mono-nums">({counts[f.key] || 0})</span>
          </button>
        ))}
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="dilekce-search" value={q} onChange={e => setQ(e.target.value)}
                 placeholder="Ad, TC veya IBAN ara..."
                 className="pl-9 pr-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm bg-white w-64" />
        </div>
      </div>

      <div className="crisp-card overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Yükleniyor...</div>
        ) : visible.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <Inbox size={26} className="mx-auto mb-2 text-slate-400"/>
            <div className="text-sm">Bu filtrede dilekçe yok.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Tarih</th>
                  <th className="text-left px-4 py-3">Ortak</th>
                  <th className="text-left px-4 py-3">Şirket</th>
                  <th className="text-right px-4 py-3">Hisse</th>
                  <th className="text-left px-4 py-3">IBAN</th>
                  <th className="text-left px-4 py-3">Durum</th>
                  <th className="text-right px-4 py-3">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(d => {
                  const st = d.status || "new";
                  const meta = STATUS_META[st] || STATUS_META.new;
                  return (
                    <tr key={d.id} data-testid={`dilekce-row-${d.id}`} className="border-t border-slate-100 hover:bg-slate-50 align-top">
                      <td className="px-4 py-3 font-mono-nums text-slate-700 whitespace-nowrap">{fmtDate(d.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{d.member_ad_soyad}</div>
                        <div className="text-[11px] text-slate-500 font-mono-nums">{d.member_tc || "—"}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${COMPANY_CLS[d.company] || ""}`}>
                          {COMPANY_LABELS[d.company] || d.company}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono-nums font-bold text-slate-900">{d.hisse}</td>
                      <td className="px-4 py-3 font-mono-nums text-slate-700 tracking-wider text-[12px]">{d.iban}</td>
                      <td className="px-4 py-3">
                        <span data-testid={`dilekce-status-${d.id}`} className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${meta.cls}`}>
                          {meta.label}
                        </span>
                        {d.yk_karar_no && (
                          <div className="text-[11px] text-slate-500 mt-1">YK: <span className="font-mono-nums font-semibold text-slate-700">{d.yk_karar_no}</span></div>
                        )}
                        {d.review_note && (
                          <div className="text-[11px] text-slate-500 mt-1 max-w-[220px] truncate" title={d.review_note}>{d.review_note}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex gap-1.5 flex-wrap justify-end">
                          <button data-testid={`dilekce-dl-${d.id}`} onClick={() => download(d.id, d.member_ad_soyad)}
                            title="DOCX indir"
                            className="inline-flex items-center gap-1 text-[12px] font-semibold text-slate-700 hover:text-slate-900 border border-slate-200 rounded-md px-2 py-1 bg-white hover:bg-slate-50">
                            <FileText size={12}/> DOCX
                          </button>
                          {st === "new" && (
                            <button data-testid={`dilekce-review-${d.id}`} onClick={() => markReviewed(d.id)}
                              title="İncelendi olarak işaretle"
                              className="inline-flex items-center gap-1 text-[12px] font-semibold text-blue-700 border border-blue-200 rounded-md px-2 py-1 bg-white hover:bg-blue-50">
                              <Eye size={12}/> İncele
                            </button>
                          )}
                          {st !== "approved" && st !== "rejected" && (
                            <>
                              <button data-testid={`dilekce-approve-${d.id}`} onClick={() => setApproveOn(d)}
                                className="inline-flex items-center gap-1 text-[12px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md px-2 py-1">
                                <Check size={12}/> Onayla
                              </button>
                              <button data-testid={`dilekce-reject-${d.id}`} onClick={() => setRejectOn(d)}
                                className="inline-flex items-center gap-1 text-[12px] font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md px-2 py-1">
                                <X size={12}/> Reddet
                              </button>
                            </>
                          )}
                          <button data-testid={`dilekce-del-${d.id}`} onClick={() => remove(d.id)}
                            title="Dilekçeyi sil"
                            className="inline-flex items-center gap-1 text-[12px] font-semibold text-red-600 hover:text-red-700 border border-red-200 rounded-md px-2 py-1 bg-white hover:bg-red-50">
                            <Trash2 size={12}/>
                          </button>
                        </div>
                        {(st === "approved" || st === "rejected") && d.reviewer_name && (
                          <div className="text-[10px] text-slate-400 mt-1.5">{d.reviewer_name} · {fmtDate(d.reviewed_at)}</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ApproveDialog open={!!approveOn} dilekce={approveOn} onClose={() => setApproveOn(null)}
                     onSubmit={(body) => patch(approveOn.id, body)} />
      <RejectDialog open={!!rejectOn} dilekce={rejectOn} onClose={() => setRejectOn(null)}
                    onSubmit={(body) => patch(rejectOn.id, body)} />
    </div>
  );
}
